// Copilot query engine — interprets a natural-language command against the
// practice's data and returns a grounded answer + entities + suggested actions.
// This stands in for an LLM tool-calling loop; in Phase 2 it routes to the
// model with these same selectors exposed as tools.

import {
  MOCK_PATIENTS, MOCK_CLAIMS, MOCK_PLANS, MOCK_WORKFLOW_ITEMS,
} from '../mock';
import {
  buildWorklist, latestCheckForPatient, scoreCheck, patientById, payerById,
  fullName, money, claimsStats,
} from './data';
import { claimsEnabled, estimatorEnabled } from './features';
// NOTE: the deterministic simulation (runCopilot below) is still mock-backed —
// runAnalytics()/find-patients/list-by-condition run synchronously against the
// mock dataset because Copilot.jsx calls runCopilot() synchronously. The live
// path (runCopilotLive) goes through the real v1 API via the shared client.
import { runAnalytics } from './analytics';
import { askCopilot, runAnalyticsQuery } from '../api/hooks';

// Live backend (real Claude) via the shared API client. The UI only calls this
// when the user opts into "Live AI"; on any failure it falls back to the
// deterministic simulation (runCopilot) — so the demo never depends on it.
export async function runCopilotLive(message, practiceId = 'prac_001') {
  // Analytical / chart-shaped questions resolve to a grounded answer + chart spec.
  if (isChartQuestion(message)) {
    const a = await runAnalyticsQuery(message, practiceId);
    if (a && a.chartable) {
      return { answer: a.answer, items: a.items || [], actions: [], chart: a.chart, mode: 'live' };
    }
    // Not chartable — fall through to the conversational copilot.
  }
  const out = await askCopilot(message, practiceId); // { answer, toolCalls, items, mode }
  return { actions: [], ...out, mode: out.mode || 'live' };
}

// Heuristic for routing to the analytics endpoint (chart/visualization intents).
function isChartQuestion(message) {
  const q = (message || '').toLowerCase();
  return /\b(chart|graph|plot|visuali[sz]e|trend|over time|by (payer|practice|provider|plan|month|week|day)|breakdown|distribution|volume)\b/.test(q);
}

// Suggestions adapt to enabled modules — the denial prompt only shows when
// the Claims & Denials module is on.
export function suggestions() {
  return [
    'Chart appointments by payer',
    'Eligibility volume over time',
    'Network mix across responses',
    'Projected revenue by practice',
    'Who has inactive coverage?',
    claimsEnabled() && 'How much is at stake in denials?',
    'Why was Sofia flagged?',
  ].filter(Boolean);
}
// Back-compat static export (full list).
export const SUGGESTIONS = [
  'Who has inactive coverage?',
  'Show high-risk appointments today',
  'Which patients need a referral?',
  'How much is at stake in denials?',
  'Why was Sofia flagged?',
  'Draft comms for out-of-network patients',
];

const item = (label, sub, href, badge) => ({ label, sub, href, badge });

const NAV = [
  { kw: ['worklist', 'inbox', 'home'], label: 'Worklist', href: '/app' },
  { kw: ['workflow', 'queue', 'referral', 'authorization', 'auth', 'comms', 'data sync'], label: 'Workflows', href: '/app/appointments' },
  { kw: ['agent', 'activity', 'audit'], label: 'Agent activity', href: '/app/agents' },
  { kw: ['estimate', 'estimator', 'collect'], label: 'Estimator', href: '/app/estimator' },
  { kw: ['insight', 'pattern', 'retrospective', 'denial', 'denied', 'appeal'], label: 'Insights', href: '/app/insights' },
  { kw: ['finance', 'cfo', 'collections', 'budget'], label: 'Finance', href: '/app/finance' },
  { kw: ['appointment', 'schedule'], label: 'Appointments', href: '/app/appointments' },
  { kw: ['patient', 'roster'], label: 'Patients', href: '/app/patients' },
  { kw: ['network', 'plan', 'contracted'], label: 'Network', href: '/app/network' },
  { kw: ['contract', 'fee schedule'], label: 'Contracts', href: '/app/contracts' },
  { kw: ['policy', 'policies', 'governance'], label: 'Policies', href: '/app/policies' },
  { kw: ['setting', 'rule', 'automation'], label: 'Settings', href: '/app/settings' },
];

export function runCopilot(query, practiceId = 'prac_001') {
  const q = (query || '').toLowerCase().trim();
  if (!q) return { answer: null, items: [], actions: [] };

  // 0) analytical question → grounded answer + an ECharts spec to render
  const analytics = runAnalytics(query, practiceId);
  if (analytics) return { answer: analytics.answer, items: analytics.items || [], actions: [], chart: analytics.chart };

  const worklist = buildWorklist(practiceId);
  const checkHref = (patientId) => { const c = latestCheckForPatient(patientId); return c ? `/app/eligibility/${c.id}` : '/app/patients'; };

  // 1) "why was X flagged" — explain a patient's risk
  const whyMatch = /why (?:was|is)?\s*([a-z]+)/.exec(q);
  if (q.includes('why') && whyMatch) {
    const name = whyMatch[1];
    const pat = MOCK_PATIENTS.find((p) => p.firstName.toLowerCase() === name || p.lastName.toLowerCase() === name);
    if (pat) {
      const r = scoreCheck(latestCheckForPatient(pat.id), pat);
      return {
        answer: `${fullName(pat)} scored ${r.score} (${r.level}). Drivers: ${r.factors.map((f) => `${f.label} +${f.points}`).join(', ') || 'none'}.`,
        items: [item(fullName(pat), `Risk ${r.score} · ${r.reasons.join(' · ')}`, checkHref(pat.id), r.level)],
        actions: [{ label: 'Open verification', href: checkHref(pat.id) }],
      };
    }
  }

  // 2) inactive coverage
  if (q.includes('inactive') || (q.includes('coverage') && q.includes('not'))) {
    const hits = worklist.filter((w) => w.check?.coverage?.active === false);
    return {
      answer: `${hits.length} patient${hits.length === 1 ? '' : 's'} with inactive coverage. The Communication Agent has drafts ready.`,
      items: hits.map((w) => item(fullName(w.patient), `${w.plan?.planName} · coverage ended`, checkHref(w.patient.id), 'high')),
      actions: [{ label: 'Open patient-comms queue', href: '/app/appointments' }],
    };
  }

  // 3) out of network
  if (q.includes('out of network') || q.includes('out-of-network') || q.includes('oon')) {
    const hits = worklist.filter((w) => w.check?.networkStatusResolved === 'out_of_network');
    return {
      answer: `${hits.length} out-of-network appointment${hits.length === 1 ? '' : 's'} today — higher patient cost-share, route to estimate + auth.`,
      items: hits.map((w) => item(fullName(w.patient), `${w.plan?.planName} · ${w.payer?.name}`, checkHref(w.patient.id), 'high')),
      actions: [{ label: 'Draft estimate notices', href: '/app/appointments' }],
    };
  }

  // 4) referral
  if (q.includes('referral')) {
    const hits = MOCK_WORKFLOW_ITEMS.filter((w) => w.queue === 'referral' && w.status === 'awaiting_review');
    return {
      answer: `${hits.length} appointment${hits.length === 1 ? '' : 's'} need a referral confirmed before the visit.`,
      items: hits.map((w) => item(fullName(patientById(w.patientId)), w.title, '/app/appointments', w.priority)),
      actions: [{ label: 'Open referral queue', href: '/app/appointments' }],
    };
  }

  // 5) prior auth
  if (q.includes('prior auth') || q.includes('authorization') || (q.includes('auth') && !q.includes('author'))) {
    const hits = MOCK_WORKFLOW_ITEMS.filter((w) => w.queue === 'authorization' && w.status === 'awaiting_review');
    return {
      answer: `${hits.length} authorization${hits.length === 1 ? '' : 's'} pending (incl. surgical).`,
      items: hits.map((w) => item(fullName(patientById(w.patientId)), w.title, '/app/appointments', w.priority)),
      actions: [{ label: 'Open authorization queue', href: '/app/appointments' }],
    };
  }

  // 6) denials / money at stake — only when the Claims & Denials module is on
  if (claimsEnabled() && (q.includes('denial') || q.includes('denied') || q.includes('at stake') || q.includes('appeal'))) {
    const s = claimsStats(practiceId);
    const denied = MOCK_CLAIMS.filter((c) => c.practiceId === practiceId && c.status === 'denied');
    const atRisk = denied.reduce((a, c) => a + c.totalCharge, 0);
    return {
      answer: `${denied.length} denied claims totaling ${money(atRisk)} at risk (denial rate ${Math.round(s.denialRate * 100)}%). Each can be appealed.`,
      items: denied.map((c) => item(`${c.id} · ${money(c.totalCharge)}`, `${fullName(patientById(c.patientId))} · ${c.denialReason}`, '/app/insights', 'denied')),
      actions: [{ label: 'Open denial insights', href: '/app/insights' }],
    };
  }

  // 7) high risk
  if (q.includes('high risk') || q.includes('high-risk') || (q.includes('risk') && !q.includes('why'))) {
    const hits = worklist.filter((w) => w.risk.level === 'high');
    return {
      answer: `${hits.length} high-risk appointment${hits.length === 1 ? '' : 's'} today.`,
      items: hits.map((w) => item(fullName(w.patient), `Risk ${w.risk.score} · ${w.risk.reasons[0]}`, checkHref(w.patient.id), 'high')),
      actions: [{ label: 'Open worklist (high risk)', href: '/app' }],
    };
  }

  // 8) draft comms (action intent)
  if (q.includes('draft') || (q.includes('send') && q.includes('message'))) {
    const hits = MOCK_WORKFLOW_ITEMS.filter((w) => w.queue === 'communication' && w.status === 'awaiting_review');
    return {
      answer: `${hits.length} patient message${hits.length === 1 ? '' : 's'} drafted and waiting for your approval.`,
      items: hits.map((w) => item(fullName(patientById(w.patientId)), w.title, `/app/comms/${w.id}`, w.priority)),
      actions: [{ label: 'Open patient-comms queue', href: '/app/appointments' }],
    };
  }

  // 9) entity search — patients, plans, claims
  const patHits = MOCK_PATIENTS.filter((p) => p.practiceId === practiceId && `${p.firstName} ${p.lastName} ${p.memberIdLast4}`.toLowerCase().includes(q));
  const planHits = MOCK_PLANS.filter((p) => p.planName.toLowerCase().includes(q));
  const claimHits = claimsEnabled() ? MOCK_CLAIMS.filter((c) => c.id.toLowerCase().includes(q)) : [];
  if (patHits.length || planHits.length || claimHits.length) {
    return {
      answer: null,
      items: [
        ...patHits.map((p) => item(fullName(p), `Patient · ${payerById(p.payerId)?.name}`, checkHref(p.id))),
        ...planHits.map((p) => item(p.planName, `Plan · ${p.planType}`, '/app/network')),
        ...claimHits.map((c) => item(c.id, `Claim · ${c.status}`, '/app/insights')),
      ].slice(0, 8),
      actions: [],
    };
  }

  // 10) navigation fallback
  const nav = NAV
    .filter((n) => n.kw.some((k) => q.includes(k)))
    .filter((n) => n.href !== '/app/estimator' || estimatorEnabled());
  if (nav.length) return { answer: null, items: nav.map((n) => item(n.label, 'Go to page', n.href)), actions: [] };

  return { answer: `No grounded match for “${query}”. Try a patient name, a plan, or one of the suggestions.`, items: [], actions: [] };
}
